import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { createAxelClient, type AxelAPIClient } from '../services/axel-api';
import { UserType } from '../../types/user';
import { getCognitoTokens } from '../actions/user';

// Whitelisted emails that can access Axel features
// TODO: Remove this whitelist after broader testing
const AXEL_ALLOWED_EMAILS = [
  'axelalsowrite@axel.io',
  'axelreadonly@axel.io',
  'axelreceiver@axel.io',
  'p.developer@pdm.ai',
  'gsp@pdm.ai',
];

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserType | null;
  jwtToken: string | null;
  jwtReady: boolean;
  apiClient: AxelAPIClient | null;
  canAccessAxel: boolean;
  refreshJWT: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Get Cognito JWT directly from localStorage.
 * Returns null if token is missing or expired.
 */
const getCognitoJwt = (): string | null => {
  const cognitoTokens = getCognitoTokens();
  if (!cognitoTokens?.access_token) {
    return null;
  }

  // Check expiration
  if (Date.now() >= cognitoTokens.expires_at) {
    return null;
  }

  return cognitoTokens.access_token;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Get user from Redux state
  const user = useSelector((state: any) => state.user?.user || null);
  const isLoading = useSelector((state: any) => state.user?.isLoading || false);

  // JWT token state for Axel API - uses Cognito JWT directly
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [fetchingJwt, setFetchingJwt] = useState(false);

  // Derive auth state from Redux user (user exists and has logged in)
  const isAuthenticated = Boolean(user?.id);

  // Check if user has access to Axel features
  const canAccessAxel = useMemo(() => {
    if (!user?.email) return false;
    return AXEL_ALLOWED_EMAILS.includes(user.email);
  }, [user?.email]);

  // Refresh JWT by re-reading Cognito tokens from localStorage
  const refreshJWT = useCallback(async (): Promise<string | null> => {
    const newToken = getCognitoJwt();
    setJwtToken(newToken);
    return newToken;
  }, []);

  // Initialize JWT from Cognito tokens when component mounts or user changes
  useEffect(() => {
    if (!canAccessAxel || fetchingJwt) {
      return;
    }

    setFetchingJwt(true);
    try {
      const cognitoJwt = getCognitoJwt();
      setJwtToken(cognitoJwt);
    } finally {
      setFetchingJwt(false);
    }
  }, [canAccessAxel, fetchingJwt, user?.id]);

  // Listen for localStorage changes to detect Cognito token updates
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'paramount-cognito-tokens' && canAccessAxel) {
        const newJwt = getCognitoJwt();
        setJwtToken(newJwt);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [canAccessAxel]);

  // JWT is ready when we have it OR when user doesn't have Axel access
  const jwtReady = !canAccessAxel || (canAccessAxel && Boolean(jwtToken) && !fetchingJwt);

  // Create Axel API client using the library's factory
  // Authentication uses Cognito JWT exclusively - the backend validates via JWKS
  const apiClient = useMemo(() => {
    if (!jwtToken) {
      return null;
    }

    const baseURL = process.env.REACT_APP_AXEL_API_URL;
    if (!baseURL) {
      console.error('REACT_APP_AXEL_API_URL is not configured');
      return null;
    }

    return createAxelClient(jwtToken, baseURL);
  }, [jwtToken]);

  const value: AuthContextType = useMemo(
    () => ({
      isAuthenticated,
      isLoading: isLoading || fetchingJwt,
      user,
      jwtToken,
      jwtReady,
      apiClient,
      canAccessAxel,
      refreshJWT,
    }),
    [
      isAuthenticated,
      isLoading,
      fetchingJwt,
      user,
      jwtToken,
      jwtReady,
      apiClient,
      canAccessAxel,
      refreshJWT,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
